import React, { useRef, useState, useCallback } from 'react';
import DocumentEditor from './DocumentEditor';
import VariablePanel from './VariablePanel';
import PreviewView from './PreviewView';
import { renderTemplate } from '../../utils/templateRenderer';
import { sanitizeHtml } from './sanitizeHtml';
import { hasTokenKey, isUniqueTokenKey } from './tokenUniquity';
import { odtToHtml, htmlToOdt } from '../../utils/odtConverter';
import './TemplateEditorLayout.css';
import './DocumentEditor.css';

export default function TemplateEditorLayout({
  documentValue,
  onDocumentChange,
  onInsertVariable,
  onTokenBlocked,
  isPreviewMode = false,
  previewData = {},
  templateType = 'invoice',
}) {
  const bodyRef = useRef(null);
  const templateUploadInputRef = useRef(null);
  const [pageSettings] = useState({
    paperSize: 'letter',
    marginTop: 1,
    marginRight: 1,
    marginBottom: 1,
    marginLeft: 1,
    fontFamily: 'Liberation Serif, Times New Roman, serif',
    fontSize: 12,
  });
  // Sanitize document before passing to parent
  const safeDocumentChange = useCallback(
    (value) => {
      if (onDocumentChange) onDocumentChange(sanitizeHtml(value || ''));
    },
    [onDocumentChange]
  );

  // Single full-page editor: combined content for uniquity check
  const getCombinedHtmlForUniquity = useCallback(() => {
    return documentValue || '';
  }, [documentValue]);

  const handleTemplateUpload = useCallback(
    async (e) => {
      const file = e.target?.files?.[0];
      e.target.value = '';
      if (!file || !safeDocumentChange) return;
      const ext = (file.name || '').toLowerCase().slice(-4);
      const isOdt = ext === '.odt' || file.name?.toLowerCase().endsWith('.odt');
      if (isOdt) {
        try {
          const buf = await file.arrayBuffer();
          const html = await odtToHtml(buf);
          if (html) safeDocumentChange(html);
        } catch (err) {
          if (onTokenBlocked) onTokenBlocked(err.message || 'Failed to read ODT file.');
        }
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          const html = typeof reader.result === 'string' ? reader.result : '';
          if (html) safeDocumentChange(html);
        };
        reader.readAsText(file, 'UTF-8');
      }
    },
    [safeDocumentChange, onTokenBlocked]
  );

  const handleTemplateDownload = useCallback(async () => {
    const html = documentValue || '';
    try {
      const odtBuffer = await htmlToOdt(html);
      const blob = new Blob([odtBuffer], {
        type: 'application/vnd.oasis.opendocument.text',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template.odt';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (onTokenBlocked) onTokenBlocked(err.message || 'Failed to create ODT file.');
    }
  }, [documentValue, onTokenBlocked]);

  // Variable insertion into the single editor; enforce unique tokens
  const handleInsertVariable = (variable) => {
    if (isUniqueTokenKey(variable.key)) {
      const combined = getCombinedHtmlForUniquity();
      if (hasTokenKey(combined, variable.key)) {
        if (onTokenBlocked) {
          onTokenBlocked(`"${variable.token}" can only be used once in the template.`);
        }
        return;
      }
    }

    if (bodyRef?.current?.insertVariable) {
      const editor = bodyRef.current.getEditor();
      if (editor && isUniqueTokenKey(variable.key)) {
        const html = editor.getData();
        if (hasTokenKey(html, variable.key)) {
          if (onTokenBlocked) onTokenBlocked(`"${variable.token}" can only be used once.`);
          return;
        }
      }
      bodyRef.current.insertVariable(variable);
    } else if (onInsertVariable) {
      onInsertVariable('body', variable);
    }
  };

  // Create mock preview data (matches real data structure)
  const getPreviewData = () => {
    const items = [
      { description: 'Sample Item 1', quantity: 1, unit_price: 100.00, taxable: true, total: 100.00 },
      { description: 'Sample Item 2', quantity: 2, unit_price: 50.00, taxable: true, total: 100.00 }
    ];
    
    return {
      client: {
        name: 'John Smith',
        address: '123 Main Street',
        city: 'San Francisco',
        state: 'CA',
        zip: '94102',
        city_state_zip: 'San Francisco, CA 94102',
        email: 'john.smith@example.com',
        phone: '(555) 123-4567',
      },
      invoice: {
        number: 'INV-2024-001',
        date: 'January 15, 2024',
        due_date: 'February 15, 2024',
        document_type: templateType === 'estimate' ? 'ESTIMATE' : 'INVOICE',
        subtotal: '$1,250.00',
        tax: '$137.50',
        tax_amount: '$137.50',
        total: '$1,387.50',
        tax_rate: 11,
        include_tax: true,
        taxable_subtotal: 1250.00,
        non_taxable_subtotal: 0,
        line_items: items,
        items_table: items,
        notes: 'Thank you for your business!',
      },
      date: 'January 15, 2024',
      due_date: 'February 15, 2024',
      facility: {
        name: 'Menlo Atherton Glass',
        address: 'PO Box 5214, Redwood City CA 94063',
      },
      user: {
        name: 'Jane Doe',
      },
      notes_section: 'Thank you for your business!',
      tax_rate: 11,
    };
  };

  // If in preview mode, show PDF-style preview using document content
  if (isPreviewMode) {
    const previewData = getPreviewData();
    const previewContent = documentValue
      ? renderTemplate(documentValue, previewData, false)
      : '';
    return (
      <div className="template-editor-layout preview-mode">
        <PreviewView
          templateContent={previewContent}
          templateType={templateType}
          pageSettings={pageSettings}
        />
      </div>
    );
  }

  return (
    <div className="letter-editor-container">
      <input
        ref={templateUploadInputRef}
        type="file"
        accept=".odt,.html,.htm"
        style={{ display: 'none' }}
        onChange={handleTemplateUpload}
      />
      <VariablePanel onInsertVariable={handleInsertVariable} templateType={templateType} />
      <div className="document-editor-area">
        <div className="template-editor-toolbar-extra">
          <button
            type="button"
            className="btn-editor-upload"
            onClick={() => templateUploadInputRef.current?.click()}
            title="Upload template (ODT or HTML)"
          >
            Upload
          </button>
          <button
            type="button"
            className="btn-editor-download"
            onClick={handleTemplateDownload}
            title="Download template as ODT"
          >
            Download
          </button>
        </div>
        <div className="document-page document-page-single-editor">
          <DocumentEditor
            ref={bodyRef}
            value={documentValue || ''}
            onChange={safeDocumentChange}
            placeholder="Start typing or insert merge variables..."
            onDropVariable={handleInsertVariable}
            className="section-editor full-page-editor"
          />
        </div>
      </div>
    </div>
  );
}
